import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface AudienceSnapshot {
  id: string;
  segmentId: string;
  sourceEvaluatedAtUtc: string;
  contentHash: string;
  partyCount: number;
  correlationRef: string;
  isIdempotentReplay: boolean;
}

export interface DistributionRun {
  id: string;
  audienceSnapshotId: string;
  campaignId: string | null;
  contentAssetId: string | null;
  publishPlanId: string | null;
  channelKey: string;
  purposeKey: string;
  locale: string;
  templateKey: string | null;
  subject: string;
  status: string;
  scheduledAtUtc: string | null;
  isTestSend: boolean;
  correlationId: string;
  totalCount: number;
  eligibleCount: number;
  suppressedCount: number;
  queuedCount: number;
  sentCount: number;
  deliveredCount: number;
  failedCount: number;
  bouncedCount: number;
  complainedCount: number;
  previewReasonCounts: Record<string, number>;
  approvalReference: string | null;
  approvedByUserId: string | null;
  approvedAtUtc: string | null;
  liveUpdateTopic: string;
}

export interface DistributionPreviewResult {
  runId: string;
  totalCount: number;
  eligibleCount: number;
  suppressedCount: number;
  reasonCounts: Record<string, number>;
}

export interface DistributionRecipientSummary {
  partyId: string;
  partyDisplayName: string;
  status: string;
  previewReasonCode: string | null;
}

export interface PagedDistributionRuns {
  items: DistributionRun[];
  totalCount: number;
  skip: number;
  take: number;
}

export interface PagedDistributionRecipients {
  items: DistributionRecipientSummary[];
  totalCount: number;
  skip: number;
  take: number;
}

export interface TestSendResult {
  interactionId: string | null;
  status: string;
  externalMessageId: string | null;
  providerKey: string | null;
  isSandbox: boolean;
  isIdempotentReplay: boolean;
  blockReason: string | null;
}

export interface GrowthCommsDistributionLiveUpdatePayload {
  reason?: string;
  runId?: string;
  occurredAtUtc?: string;
}

@Injectable({ providedIn: 'root' })
export class GrowthCommsDistributionService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/api/v1/growth/comms/distributions`;

  createSnapshot(payload: { segmentId: string; idempotencyKey: string; correlationRef?: string | null }): Observable<AudienceSnapshot> {
    return this.http.post<AudienceSnapshot>(`${this.base}/snapshots`, payload);
  }

  createRun(payload: {
    audienceSnapshotId: string;
    campaignId?: string | null;
    contentAssetId?: string | null;
    publishPlanId?: string | null;
    channelKey: string;
    purposeKey: string;
    locale?: string | null;
    templateKey?: string | null;
    subject: string;
    body: string;
    idempotencyKey: string;
    correlationId?: string | null;
    scheduledAtUtc?: string | null;
  }): Observable<DistributionRun> {
    return this.http.post<DistributionRun>(this.base, payload);
  }

  previewRun(runId: string): Observable<DistributionPreviewResult> {
    return this.http.post<DistributionPreviewResult>(`${this.base}/${runId}/preview`, {});
  }

  submitApproval(runId: string): Observable<DistributionRun> {
    return this.http.post<DistributionRun>(`${this.base}/${runId}/submit-approval`, {});
  }

  dispatch(runId: string): Observable<DistributionRun> {
    return this.http.post<DistributionRun>(`${this.base}/${runId}/dispatch`, {});
  }

  cancel(runId: string): Observable<DistributionRun> {
    return this.http.post<DistributionRun>(`${this.base}/${runId}/cancel`, {});
  }

  testSend(payload: {
    partyId: string;
    channelKey: string;
    purposeKey: string;
    subject: string;
    body: string;
    templateKey?: string | null;
    idempotencyKey: string;
    correlationId?: string | null;
  }): Observable<TestSendResult> {
    return this.http.post<TestSendResult>(`${this.base}/test-send`, payload);
  }

  listRuns(status?: string | null, skip = 0, take = 25): Observable<PagedDistributionRuns> {
    let params = new HttpParams();
    if (status) params = params.set('status', status);
    params = params.set('skip', String(skip)).set('take', String(take));
    return this.http.get<PagedDistributionRuns>(this.base, { params });
  }

  getRun(runId: string): Observable<DistributionRun> {
    return this.http.get<DistributionRun>(`${this.base}/${runId}`);
  }

  listRecipients(runId: string, skip = 0, take = 25): Observable<PagedDistributionRecipients> {
    const params = new HttpParams().set('skip', String(skip)).set('take', String(take));
    return this.http.get<PagedDistributionRecipients>(`${this.base}/${runId}/recipients`, { params });
  }
}
